/**
 * Logistics Handlers
 *
 * Pure command handlers for the Logistics subdomain. Mirrors the Invoicing
 * subdomain 1:1: provisioned integrations are keyed in `data.integrations`, the
 * add modal is a single boolean + selected connector, and submit/delete
 * lifecycles are tracked through `loading.submitting` and the integrations
 * record respectively.
 */

import { LogisticsState } from './state';
import { LogisticsCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<LogisticsCommand, { type: T }>;

// List handlers
const handleListSet = (
  state: LogisticsState,
  cmd: ExtractCommand<'integrations/logistics/list/set'>
): LogisticsState => ({
  ...state,
  data: { ...state.data, integrations: cmd.payload.integrations },
  loading: { ...state.loading, listLoading: false }
});

// Add modal handlers
const handleAddModalOpen = ( state: LogisticsState ): LogisticsState => ({
  ...state,
  ui: { ...state.ui, addModalOpen: true, selectedConnectorId: null }
});

const handleAddModalClose = ( state: LogisticsState ): LogisticsState => ({
  ...state,
  ui: { ...state.ui, addModalOpen: false, selectedConnectorId: null },
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

const handleAddModalSelectConnector = (
  state: LogisticsState,
  cmd: ExtractCommand<'integrations/logistics/addModal/selectConnector'>
): LogisticsState => ({
  ...state,
  ui: { ...state.ui, selectedConnectorId: cmd.payload.connectorId }
});

// Submit handlers
const handleSubmitStart = (
  state: LogisticsState,
  cmd: ExtractCommand<'integrations/logistics/submit/start'>
): LogisticsState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  pendingSubmission: {
    connectorId: cmd.payload.connectorId,
    credentials: cmd.payload.credentials,
    label: cmd.payload.label
  }
});

const handleSubmitSuccess = (
  state: LogisticsState,
  cmd: ExtractCommand<'integrations/logistics/submit/success'>
): LogisticsState => ({
  ...state,
  data: {
    ...state.data,
    integrations: {
      ...state.data.integrations,
      [cmd.payload.integration.id]: cmd.payload.integration
    }
  },
  ui: { ...state.ui, addModalOpen: false, selectedConnectorId: null },
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

const handleSubmitError = ( state: LogisticsState ): LogisticsState => ({
  ...state,
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

// Delete handlers
const handleDeleteStart = (
  state: LogisticsState,
  cmd: ExtractCommand<'integrations/logistics/delete/start'>
): LogisticsState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  pendingDeletion: { integrationId: cmd.payload.integrationId }
});

const handleDeleteSuccess = (
  state: LogisticsState,
  cmd: ExtractCommand<'integrations/logistics/delete/success'>
): LogisticsState => {
  const { [cmd.payload.integrationId]: _, ...rest } = state.data.integrations;
  return {
    ...state,
    data: { ...state.data, integrations: rest },
    loading: { ...state.loading, submitting: false },
    pendingDeletion: null
  };
};

const handleDeleteError = ( state: LogisticsState ): LogisticsState => ({
  ...state,
  loading: { ...state.loading, submitting: false },
  pendingDeletion: null
});

export const handlers: CommandHandlerMap<LogisticsState, LogisticsCommand> = {
  'integrations/logistics/list/set': handleListSet,
  'integrations/logistics/addModal/open': handleAddModalOpen,
  'integrations/logistics/addModal/close': handleAddModalClose,
  'integrations/logistics/addModal/selectConnector': handleAddModalSelectConnector,
  'integrations/logistics/submit/start': handleSubmitStart,
  'integrations/logistics/submit/success': handleSubmitSuccess,
  'integrations/logistics/submit/error': handleSubmitError,
  'integrations/logistics/delete/start': handleDeleteStart,
  'integrations/logistics/delete/success': handleDeleteSuccess,
  'integrations/logistics/delete/error': handleDeleteError
};
